import api from "@/services/api.service";
import { ActionTree } from "vuex";
import { syncState, ISyncSystem, ISyncEntry, ITaskDynamicDataTypes, ISyncType } from "./types";
import { RootState } from "../types";
import Vue from "vue";

export const actions: ActionTree<syncState, RootState> = {
	async getSyncSystems({ commit }, pageIndex: number = 1, pageSize: number = 30) {
		if (pageIndex == 1) {
			commit("setSyncSystemsIsBusyState", true);
		}

		// Trigger commit to update our index.
		commit("setSyncSystemPageNumber", pageIndex);

		const { data } = await api.fetchAllSyncSystems(pageIndex, pageSize);
		if (data && data.length > 0) {
			commit("setSyncSystems", data);

			if (pageSize > data.length) {
				commit("setSyncSystemsAtEndStatus", true);
			}

			if (pageIndex == 1) {
				commit("setSyncSystemsIsBusyState", false);
			}
		} else {
			commit("setSyncSystemsIsBusyState", false);
			commit("setSyncSystemsAtEndStatus", true);
		}
	},
	async querySyncSystem({ commit }, payload: any) {
		// We want to verify that we got filter content.
		if (payload.filter === "") return;
		commit("setSyncSystemFilteredPageIndex", payload.index);

		if (payload.index == 1) {
			commit("resetQueriedSyncSystems");
			commit("setSyncSystemsIsBusyState", true);
		}

		const { data } = await api.querySyncSystems(payload.index, payload.pageSize, payload.filter);

		if (data && data.length > 0) {
			commit("setQueriedSyncSystems", data);

			if (payload.pageSize > data.length) {
				commit("setSyncSystemsAtEndStatus", true);
			}

			if (payload.index == 1) {
				commit("setSyncSystemsIsBusyState", false);
			}
		} else {
			commit("setSyncSystemsIsBusyState", false);
			commit("setSyncSystemsAtEndStatus", true);
		}
	},
	async getServerTypesWithCanSync({ commit }) {
		const { data } = await api.fetchServerTypesWithCanSync();
		commit("setServerYypesWithCanSync", data);
	},
	async updateSyncSystem({ commit }, syncSystem: ISyncSystem) {
		const { data } = await api.updateSyncSystem(syncSystem);
		const updatedSyncSystem: ISyncSystem = data;
		commit("updateSyncSystem", updatedSyncSystem);
	},
	async saveNewSyncSystem({ commit }, syncSystem: ISyncSystem) {
		const { data } = await api.saveNewSyncSystem(syncSystem);
		commit("addNewSyncSystem", data);
	},
	async deleteSyncSystem({ commit }, syncSystemId: number) {
		await api.deleteSyncSystem(syncSystemId);
		commit("removeSyncSystem", syncSystemId);
	},

	// ** SYNC ENTRY Actions ** //
	async getSyncEntries({ commit }, pageIndex: number = 1, pageSize: number = 30) {
		if (pageIndex == 1) {
			commit("setSyncEntryIsBusyState", true);
		}

		// Trigger commit to update our index.
		commit("setSyncEntryPageNumber", pageIndex);

		const { data } = await api.fetchSyncEntries(pageIndex, pageSize);

		if (data && data.length > 0) {
			commit("setSyncEntries", data);

			const requiesStatusCheck = data.filter((entry: ISyncEntry) => !entry.lastUpdated && entry.enabled);
			commit("setRequiredStatusCheck", requiesStatusCheck);

			if (pageSize > data.length) {
				commit("setSyncEntryAtEndStatus", true);
			}

			if (pageIndex == 1) {
				commit("setSyncEntryIsBusyState", false);
			}
		} else {
			commit("setSyncEntryIsBusyState", false);
			commit("setSyncEntryAtEndStatus", true);
		}
	},
	async deleteSyncEntry({ commit }, groupSyncId: number) {
		const res = await api.deleteSyncEntry(groupSyncId);
		commit("removeSyncEntry", groupSyncId);
	},
	async updateSyncEntry({ commit }, syncEntry: ISyncEntry) {
		try {
			const { data } = await api.updateSyncEntry(syncEntry);
			const updatedSyncEntry: ISyncEntry = data;
			commit("updateSyncEntry", updatedSyncEntry);
		} catch (err) {
			if (err && err.response && err.response.status === 566) {
				commit("setUserErrorMessage", err.response.data);
				return;
			}
			Vue.prototype.$notify({
				type: "error",
				title: "Error",
				text: err
			});
		}
	},
	async saveNewSyncEntry({ commit }, syncEntry: ISyncEntry) {
		try {
			const { data } = await api.saveNewSyncEntry(syncEntry);
			commit("addNewSyncEntry", data);
		} catch (err) {
			if (err && err.response && err.response.status === 566) {
				commit("setUserErrorMessage", err.response.data);
				return;
			}
			Vue.prototype.$notify({
				type: "error",
				title: "Error",
				text: err
			});
		}
	},
	async fetchDynamicDataList({ commit }) {
		const { data } = await api.fetchDynamicDataList();
		const dynamicDataList: ITaskDynamicDataTypes[] = data;
		commit("setDynamicDataList", dynamicDataList);
	},
	async fetchSyncTypes({ commit }) {
		const { data } = await api.fetchSyncTypes();
		const syncTypes: ISyncType[] = data;
		commit("setSyncTypes", syncTypes);
	},
	async verifySyncEntries({ commit }, syncEntriesToVerify: ISyncEntry[]) {
		if (syncEntriesToVerify.length == 0) return;
		const { data } = await api.verifySyncEntries(syncEntriesToVerify);

		const containsData = data && data.length > 0;

		if (!containsData && syncEntriesToVerify.length > 0) {
			// Don't do nothing here, we only get values back that sync successfully resolved.
			return;
		}

		const entries: ISyncEntry[] = data;

		commit("updateSyncEntriesStatus", entries);
	},
	async querySyncEntry({ commit }, payload: any) {
		// We want to verify that we got filter content.
		if (payload.filter === "") return;
		commit("setSyncEntryFilteredPageIndex", payload.index);

		if (payload.index == 1) {
			commit("resetQueriedSyncEntries");
			commit("setSyncEntryIsBusyState", true);
		}

		const { data } = await api.querySyncEntries(payload.index, payload.pageSize, payload.filter);

		if (data && data.length > 0) {
			commit("setQueriedSyncEntries", data);

			if (payload.pageSize > data.length) {
				commit("setSyncEntryAtEndStatus", true);
			}

			if (payload.index == 1) {
				commit("setSyncEntryIsBusyState", false);
			}
		} else {
			commit("setSyncEntryIsBusyState", false);
			commit("setSyncEntryAtEndStatus", true);
		}
	},
	async resyncSyncEntry({ commit }, syncEntry: ISyncEntry) {
		try {
			await api.resyncSyncEntry(syncEntry.groupSyncId);
			commit("resyncSyncEntry", syncEntry);
		} catch (err) {
			Vue.prototype.$notify({
				type: "error",
				title: "Error",
				text: err
			});
		}
	}
};
